import React, { useState, useEffect, useCallback } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Mail, RefreshCw, CheckCircle2, XCircle, ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "@/context/LanguageContext";
import api from "@/lib/api";

export const EmailHistoryDialog = ({ open, onOpenChange }) => {
  const { t, lang } = useLang();
  const s = t.admin.sms;
  const e = t.admin.email_history;
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/email/logs");
      setLogs(res.data);
    } catch {
      toast.error(s.send_error);
    } finally {
      setLoading(false);
    }
  }, [s.send_error]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const fmt = (iso) => new Date(iso).toLocaleString(lang === "el" ? "el-GR" : "en-GB", { dateStyle: "short", timeStyle: "short" });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-brand-cream max-w-2xl rounded-[1.75rem] p-0 overflow-y-auto max-h-[92vh]" data-testid="email-history-dialog">
        <div className="bg-brand text-brand-cream px-6 pt-6 pb-5">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl text-brand-cream flex items-center gap-2">
              <Mail className="h-5 w-5" /> {e.title}
            </DialogTitle>
            <DialogDescription className="text-brand-cream/80">{e.subtitle}</DialogDescription>
          </DialogHeader>
        </div>

        <div className="px-6 py-6 space-y-3">
          <div className="flex justify-end">
            <Button onClick={load} variant="outline" size="sm" className="rounded-full border-brand/30 text-brand hover:bg-brand-soft/50 font-bold" data-testid="email-history-refresh">
              <RefreshCw className={`h-4 w-4 mr-1.5 ${loading ? "animate-spin" : ""}`} /> {s.history_refresh}
            </Button>
          </div>

          {logs.length === 0 && !loading ? (
            <p className="text-center text-brand-ink/50 py-10 italic" data-testid="email-history-empty">{e.empty}</p>
          ) : (
            <div className="space-y-3" data-testid="email-history-list">
              {logs.map((log) => (
                <div key={log.id} className="rounded-2xl bg-white border border-brand/10 p-4" data-testid={`email-log-${log.id}`}>
                  <div className="flex flex-wrap items-center gap-2">
                    {log.ok ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-bamboo-soft text-bamboo-deep text-[11px] font-bold px-2.5 py-0.5" data-testid={`email-log-status-${log.id}`}>
                        <CheckCircle2 className="h-3 w-3" /> {s.success}
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 rounded-full bg-rose-100 text-rose-700 text-[11px] font-bold px-2.5 py-0.5" data-testid={`email-log-status-${log.id}`}>
                        <XCircle className="h-3 w-3" /> {s.failed}
                      </span>
                    )}
                    <span className="rounded-full bg-brand-soft/60 text-brand text-[11px] font-bold px-2.5 py-0.5">
                      {e.kinds[log.kind] || log.kind}
                    </span>
                    <span className="text-xs font-bold text-brand-ink break-all">{log.to}</span>
                    <span className="text-xs text-brand-ink/50 ml-auto">{fmt(log.created_at)}</span>
                  </div>
                  <p className="mt-2 text-sm font-bold text-brand-ink">{log.subject}</p>
                  {!log.ok && log.detail && (
                    <p className="mt-1 text-xs text-rose-600 font-semibold">{log.detail}</p>
                  )}
                  <button
                    type="button"
                    onClick={() => setExpanded(expanded === log.id ? null : log.id)}
                    className="mt-2 inline-flex items-center gap-1 text-xs font-bold text-brand hover:text-brand-hover"
                    data-testid={`email-log-toggle-${log.id}`}
                  >
                    {expanded === log.id ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                    {e.view_content}
                  </button>
                  {expanded === log.id && (
                    <div
                      className="mt-2 rounded-xl border border-brand/10 overflow-hidden bg-white [&_table]:w-full"
                      dangerouslySetInnerHTML={{ __html: log.html }}
                      data-testid={`email-log-content-${log.id}`}
                    />
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default EmailHistoryDialog;
